import type { ReactNode } from 'react';
import { cx } from '../cx';

export type ReadoutSplitTone = 'remote' | 'hybrid' | 'onsite';
export type ReadoutSplit = {
  id: string;
  label: ReactNode;
  /** Share of the whole, 0 to 1. */ share: number;
  tone: ReadoutSplitTone;
  /** Formatted share ("42%"). */ display: ReactNode;
};

export type ReadoutProps = {
  label: ReactNode;
  /** The headline figure, formatted. */
  figure: ReactNode;
  /** What the figure is ("open jobs match"). */
  unit?: ReactNode;
  splits?: ReadoutSplit[];
  /** Sample or period, required ("Index today, 4 177 jobs"). */
  source: ReactNode;
  className?: string;
};

const SEG: Record<ReadoutSplitTone, string> = {
  remote: 'bg-jc-readout-remote',
  hybrid: 'bg-jc-readout-hybrid',
  onsite: 'bg-jc-readout-onsite',
};

/**
 * A small panel that is dark in every theme: a headline figure, an optional split bar and a source
 * line. Never clickable; actions sit beside it.
 */
export function Readout({ label, figure, unit, splits = [], source, className }: ReadoutProps) {
  const total = splits.reduce((sum, s) => sum + Math.max(0, s.share), 0) || 1;
  return (
    <div className={cx('flex flex-col gap-3 rounded-jc-info bg-jc-readout-bg p-5 text-jc-readout-ink', className)}>
      <span className="type-eyebrow text-jc-readout-muted">{label}</span>
      <span className="flex items-baseline gap-2">
        <span className="type-figure-lg text-jc-readout-gold">{figure}</span>
        {unit ? <span className="text-sm text-jc-readout-muted">{unit}</span> : null}
      </span>
      {splits.length ? (
        <div className="flex flex-col gap-2">
          <div aria-hidden="true" className="flex h-2.5 gap-0.5 overflow-hidden rounded-jc-info bg-jc-readout-track">
            {splits.map((s) => (
              <span key={s.id} className={SEG[s.tone]} style={{ width: `${(Math.max(0, s.share) / total) * 100}%` }} />
            ))}
          </div>
          <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
            {splits.map((s) => (
              <li key={s.id} className="flex items-center gap-1.5">
                <span aria-hidden="true" className={cx('size-2 rounded-full', SEG[s.tone])} />
                <span>{s.label}</span>
                <span className="font-semibold tabular-nums">{s.display}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <span className="text-xs text-jc-readout-muted">{source}</span>
    </div>
  );
}
